/**
 * Argus route registry — typed frontend wrapper around `shared/argus-route-registry.json`.
 *
 * Two consumers:
 * 1. Context injection (sidebar): `matchRouteContext(pathname)` → "Viewing: Test Detail".
 * 2. Navigate tool (Phase 4b gateway extension + the frontend navigate card):
 *    `buildRoutePath(key, params)` → URL string.
 *
 * The canonical source is `shared/argus-route-registry.json` at the repo root so the
 * gateway extension (be/argus-ts/src/extensions/navigate.ts) imports the same list.
 * Add new routes there, not here.
 */

import registryData from "../../../../shared/argus-route-registry.json";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type RouteContextKey =
  | "test_detail"
  | "test_run_detail"
  | "test_suite_detail"
  | "check_detail"
  | "browser_sessions_list"
  | "browser_session_detail"
  | "issue_detail";

export interface RouteContext {
  key: RouteContextKey;
  label: string;
  params: Record<string, string>;
}

export interface RouteRegistryEntry {
  key: RouteContextKey;
  pattern: string;
  description: string;
  labelTemplate: string;
  params: readonly string[];
}

interface RawRegistryEntry {
  key: string;
  pattern: string;
  description: string;
  labelTemplate: string;
  params: string[];
}

interface RawRegistry {
  version: number;
  routes: RawRegistryEntry[];
}

// ---------------------------------------------------------------------------
// Data load
// ---------------------------------------------------------------------------

const raw = registryData as unknown as RawRegistry;

export const ROUTE_REGISTRY: readonly RouteRegistryEntry[] = raw.routes.map((entry) => ({
  key: entry.key as RouteContextKey,
  pattern: entry.pattern,
  description: entry.description,
  labelTemplate: entry.labelTemplate,
  params: entry.params,
}));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert a React Router v7 pattern like "/tests/:testId" into a RegExp that
 * captures each `:paramName` segment, and also return the ordered list of
 * parameter names so we can zip matches back into a params object.
 */
function compilePattern(pattern: string): {
  regex: RegExp;
  paramNames: string[];
} {
  const paramNames: string[] = [];
  const regexSource = pattern
    .split("/")
    .map((segment) => {
      if (segment.startsWith(":")) {
        paramNames.push(segment.slice(1));
        return "([^/]+)";
      }
      // Escape regex metacharacters in literal segments.
      return segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    })
    .join("/");
  return { regex: new RegExp(`^${regexSource}/?$`), paramNames };
}

const COMPILED_PATTERNS = ROUTE_REGISTRY.map((entry) => ({
  entry,
  ...compilePattern(entry.pattern),
}));

/**
 * Match a `location.pathname` against the registry. Returns the first entry
 * whose pattern matches, or `null` when the current path doesn't correspond to
 * any Argus-tracked resource (for example `/settings`).
 */
export function matchRouteContext(pathname: string): RouteContext | null {
  for (const { entry, regex, paramNames } of COMPILED_PATTERNS) {
    const match = regex.exec(pathname);
    if (!match) continue;
    const params: Record<string, string> = {};
    paramNames.forEach((name, index) => {
      params[name] = decodeURIComponent(match[index + 1] ?? "");
    });
    return {
      key: entry.key,
      label: entry.labelTemplate,
      params,
    };
  }
  return null;
}

/**
 * Build a URL from a registry key + params. Used in Phase 4b by the navigate
 * card click handler and anywhere the UI needs to turn "I know what kind of
 * thing you want" into a concrete path.
 *
 * Throws if the registry key is unknown or if a required param is missing —
 * callers are expected to pass a complete params object.
 */
export function buildRoutePath(key: RouteContextKey, params: Record<string, string>): string {
  const entry = ROUTE_REGISTRY.find((candidate) => candidate.key === key);
  if (!entry) {
    throw new Error(`[argus route-registry] unknown key: ${key}`);
  }
  return entry.pattern
    .split("/")
    .map((segment) => {
      if (!segment.startsWith(":")) return segment;
      const paramName = segment.slice(1);
      const value = params[paramName];
      if (value === undefined) {
        throw new Error(`[argus route-registry] missing param "${paramName}" for key "${key}"`);
      }
      return encodeURIComponent(value);
    })
    .join("/");
}
